import { type ComputedRef, computed, type Ref, ref } from 'vue'
import { API_TOKEN_RESOURCES } from '@/api'

export type ScopeLevel = 'none' | 'read' | 'write'

/**
 * How wide each level is, so one can be compared with another: `write` covers `read` covers `none`. The only
 * place the ordering is written down, so nothing comparing levels can drift into a different idea of which
 * one is wider.
 */
export const LEVEL_RANK: Record<ScopeLevel, number> = { none: 0, read: 1, write: 2 }

/**
 * One `read` / `write` / `none` level per base resource — the shape a scope picker offers whether it is
 * granting an API token or a consent screen's connection (#326) — and the `<resource>:<level>` list that
 * follows from it. Starts with every resource at `none`: nothing is preselected, so a picker that never
 * touches a resource grants nothing for it.
 */
export function useScopeLevels(): { levels: Ref<Record<string, ScopeLevel>>; scopes: ComputedRef<string[]> } {
    const levels = ref<Record<string, ScopeLevel>>(Object.fromEntries(API_TOKEN_RESOURCES.map(r => [r, 'none'])))
    const scopes = computed(() =>
        API_TOKEN_RESOURCES.flatMap(resource =>
            levels.value[resource] === 'none' ? [] : [`${resource}:${levels.value[resource]}`],
        ),
    )
    return { levels, scopes }
}
